import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";

export type BlockedNavigation = {
  run: () => void;
};

export type UnsavedChangesNavigationGuard = {
  onNavigateAttempt: (navigation: BlockedNavigation) => void;
};

type UnsavedChangesNavigationContextValue = {
  activeGuard: UnsavedChangesNavigationGuard | null;
  setActiveGuard: (guard: UnsavedChangesNavigationGuard | null) => void;
  requestNavigation: (navigation: BlockedNavigation) => void;
  runWithoutGuard: (navigation: BlockedNavigation) => void;
};

const UnsavedChangesNavigationContext =
  createContext<UnsavedChangesNavigationContextValue | null>(null);

export const UnsavedChangesNavigationProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [activeGuard, setActiveGuardState] = useState<UnsavedChangesNavigationGuard | null>(
    null,
  );
  const activeGuardRef = useRef<UnsavedChangesNavigationGuard | null>(null);
  const bypassNextNavigationRef = useRef(false);

  const setActiveGuard = useCallback((guard: UnsavedChangesNavigationGuard | null) => {
    activeGuardRef.current = guard;
    setActiveGuardState(guard);
  }, []);

  const requestNavigation = useCallback((navigation: BlockedNavigation) => {
    if (bypassNextNavigationRef.current) {
      bypassNextNavigationRef.current = false;
      navigation.run();
      return;
    }

    const guard = activeGuardRef.current;

    if (guard) {
      guard.onNavigateAttempt(navigation);
      return;
    }

    navigation.run();
  }, []);

  const runWithoutGuard = useCallback((navigation: BlockedNavigation) => {
    bypassNextNavigationRef.current = true;
    navigation.run();

    if (bypassNextNavigationRef.current) {
      bypassNextNavigationRef.current = false;
    }
  }, []);

  const value = useMemo(
    () => ({
      activeGuard,
      setActiveGuard,
      requestNavigation,
      runWithoutGuard,
    }),
    [activeGuard, requestNavigation, runWithoutGuard, setActiveGuard],
  );

  return (
    <UnsavedChangesNavigationContext.Provider value={value}>
      {children}
    </UnsavedChangesNavigationContext.Provider>
  );
};

export const useUnsavedChangesNavigation = () => {
  const context = useContext(UnsavedChangesNavigationContext);

  if (!context) {
    throw new Error(
      "useUnsavedChangesNavigation must be used within UnsavedChangesNavigationProvider",
    );
  }

  return context;
};
